import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Container, 
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Devices as DevicesIcon
} from '@mui/icons-material';

// Custom hooks and services
import { useAuth } from '../../hooks/auth/useAuth';
import { useDeviceManagement } from './hooks/useDeviceManagement';

// Components
import OrganizationOverview from './components/OrganizationOverview';
import DeviceFeatures from './components/DeviceFeatures';
import DeviceTable from './components/DeviceTable';
import DeviceRegistrationDialog from './components/DeviceRegistrationDialog';
import DeviceEditDialog from './components/DeviceEditDialog';
import DeviceDeleteDialog from './components/DeviceDeleteDialog';
import NotificationSnackbar from './components/NotificationSnackbar';

const DevicePage = () => {
  // Get authenticated user data (must be called first)
  const { user, loading: authLoading, error: authError } = useAuth();

  // Use custom hook for device management (must be called before any early returns)
  const {
    organizationData,
    loading,
    error,
    devices,
    totalDevicesInOrg,
    page,
    rowsPerPage,
    loadingDevices,
    snackbar,
    handleChangePage,
    handleChangeRowsPerPage,
    showSnackbar,
    hideSnackbar,
    refreshData
  } = useDeviceManagement(user);

  // Dialog states (must be called before any early returns)
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, device: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, device: null });

  // Early return if authentication is still loading
  if (authLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading authentication...
        </Typography>
      </Container>
    );
  }

  // Handle authentication errors
  if (authError || !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">
          {authError || 'Authentication required. Please log in to access devices.'}
        </Alert>
      </Container>
    );
  }

  // Validate user has organization access
  if (!user.organizationId && user.role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="warning">
          Access denied. You must be assigned to an organization to view devices.
        </Alert>
      </Container>
    );
  }

  // Event handlers
  const handleRegisterDevice = () => {
    setRegistrationOpen(true);
  };

  const handleDeviceAction = (action, device) => {
    if (action === 'Delete') {
      setDeleteDialog({ open: true, device });
    } else if (action === 'Edit') {
      setEditDialog({ open: true, device });
    } else {
      // TODO: Implement view action
      showSnackbar(`${action} action for device ${device.id} - Coming soon!`, 'info');
    }
  };

  const handleRegistrationSuccess = (message) => {
    showSnackbar(message, 'success');
    setRegistrationOpen(false);
    refreshData();
  };

  const handleEditSuccess = (message) => {
    showSnackbar(message, 'success');
    setEditDialog({ open: false, device: null });
    refreshData();
  };

  const handleDeleteSuccess = (message) => {
    showSnackbar(message, 'success');
    setDeleteDialog({ open: false, device: null });
    refreshData();
  };

  const handleError = (message) => {
    showSnackbar(message, 'error');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <DevicesIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            ENViS Devices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI edge device management
            {user.organizationName && ` - ${user.organizationName}`}
          </Typography>
        </Box>
      </Stack>

      {/* Organization Overview */}
      <OrganizationOverview 
        organizationData={organizationData}
        loading={loading}
        error={error}
      />

      {/* Device Features */}
      <DeviceFeatures onRegisterDevice={handleRegisterDevice} />

      {/* Device Table */}
      <Paper sx={{ p: 3, borderRadius: 1, mt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <DevicesIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Registered Devices ({totalDevicesInOrg})
          </Typography>
        </Stack>

        <DeviceTable
          devices={devices}
          totalDevicesInOrg={totalDevicesInOrg}
          page={page}
          rowsPerPage={rowsPerPage}
          loadingDevices={loadingDevices}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onDeviceAction={handleDeviceAction}
        />
      </Paper>

      {/* Dialogs */}
      <DeviceRegistrationDialog
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
        organizationData={organizationData}
        currentUser={user}
        onSuccess={handleRegistrationSuccess}
        onError={handleError}
      />

      <DeviceEditDialog
        open={editDialog.open}
        device={editDialog.device}
        onClose={() => setEditDialog({ open: false, device: null })}
        organizationData={organizationData}
        currentUser={user}
        onSuccess={handleEditSuccess}
        onError={handleError}
      />

      <DeviceDeleteDialog
        open={deleteDialog.open}
        device={deleteDialog.device}
        onClose={() => setDeleteDialog({ open: false, device: null })}
        onSuccess={handleDeleteSuccess}
        onError={handleError}
      />

      {/* Notifications */}
      <NotificationSnackbar
        snackbar={snackbar}
        onClose={hideSnackbar}
      />
    </Container>
  );
};

export default DevicePage; 